import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { RecognitionPanel } from "@/components/RecognitionPanel";
import { VerdictBadge, UnattestedBadge, DriftBadge } from "@/components/CertificationBadges";
import CertifyDialog from "@/components/CertifyDialog";
import CertificationHistory from "@/components/CertificationHistory";
import { ImportReplacementPrompt } from "@/components/ImportReplacementPrompt";
import ReconcilingItemsPanel from "@/components/ReconcilingItemsPanel";
import RecurringJournalsReadinessCard from "@/components/RecurringJournalsReadinessCard";
import ExcludeDialog from "@/components/ExcludeDialog";
import ClassifyDialog from "@/components/ClassifyDialog";
import BankCertBadge from "@/components/BankCertBadge";
import BankStatementCertificationPanel from "@/components/BankStatementCertificationPanel";

const STEPS = [
  { key: "import", label: "1 · Import" },
  { key: "review", label: "2 · Review" },
  { key: "explain", label: "3 · Explain" },
  { key: "complete", label: "4 · Complete" },
];

const SOURCES = ["source", "imported", "certified", "posted"];

export default function Workspace() {
  const { id } = useParams();
  // Phase G-B · certification surfaces
  const [cert, setCert] = useState(null);          // latest Certification or null
  const [drift, setDrift] = useState(null);
  const [showCertify, setShowCertify] = useState(false);
  const [certBump, setCertBump] = useState(0);     // refresh history on change
  // Aug 2026 · Bank Statement Certification (latest across all bank batches)
  const [bankCert, setBankCert] = useState(null);

  useEffect(() => {
    if (!id) return;
    api.latestCertification(id).then(setCert).catch(() => setCert(null));
    api.governanceDrift(id).then(setDrift).catch(() => setDrift(null));
    api.periodBankCertification(id).then(setBankCert).catch(() => setBankCert(null));
  }, [id, certBump]);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState("review");
  const [focusIdx, setFocusIdx] = useState(0);
  const [filter, setFilter] = useState("under_review"); // under_review | matched | classified | all | excluded
  const [search, setSearch] = useState("");
  const [snapTab, setSnapTab] = useState("certified");
  const [drawer, setDrawer] = useState(null); // 'audit' | 'lineage' | null
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set()); // multi-select for matching
  const fileInputRef = useRef();
  const [importType, setImportType] = useState("bank");
  // GOV-030 · Import Replacement Prompt state
  const [replacementPrompt, setReplacementPrompt] = useState(null); // 409 payload
  const [pendingImportFile, setPendingImportFile] = useState(null); // File user picked
  // Exclude dialog state — the transaction under consideration, or null.
  const [excludeTarget, setExcludeTarget] = useState(null);
  // Classify dialog state — array of transactions (single-row or bulk).
  const [classifyTargets, setClassifyTargets] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await api.workspace(id);
      setData(d);
      // pick step
      if (d.import_batches.length === 0) setActiveStep("import");
      else if (d.transactions.some(t => ["imported", "under_review"].includes(t.lifecycle_status) && !t.exclusion)) setActiveStep("review");
      else if (d.latest_reconciliation_snapshot &&
        (Math.abs(d.latest_reconciliation_snapshot.bank_variance) > 0.01 || Math.abs(d.latest_reconciliation_snapshot.gl_variance) > 0.01) &&
        !d.period.variance_explanation) setActiveStep("explain");
      else if (d.period.status === "complete" || d.period.status === "locked") setActiveStep("complete");
      setLoading(false);
    } catch (e) {
      console.error(e); setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toastTimerRef = useRef(null);
  const showToast = (msg) => {
    if (toastTimerRef.current) { clearTimeout(toastTimerRef.current); toastTimerRef.current = null; }
    const isError = /fail|blocked|cannot|required|error|invalid/i.test(String(msg));
    setToast({ msg, kind: isError ? "error" : "ok" });
    if (!isError) {
      toastTimerRef.current = setTimeout(() => setToast(null), 2400);
    }
  };
  const dismissToast = () => {
    if (toastTimerRef.current) { clearTimeout(toastTimerRef.current); toastTimerRef.current = null; }
    setToast(null);
  };

  const period = data?.period;
  const txns = data?.transactions || [];

  // Filtered + searched transactions for the review queue.
  // Search is case-insensitive and matches description, reference, amount
  // (including formatted "$1,234.56"), and transaction date. An exact-amount
  // search like "13198.44" or "$13,198.44" hits any row whose amount matches.
  const visible = useMemo(() => {
    if (!txns) return [];
    const q = search.trim().toLowerCase();
    const numericQ = q ? Number(q.replace(/[$,]/g, "")) : NaN;
    return txns.filter(t => {
      if (filter === "under_review" && !(["imported", "under_review"].includes(t.lifecycle_status) && !t.exclusion)) return false;
      if (filter === "matched" && t.lifecycle_status !== "matched") return false;
      if (filter === "classified" && !(t.lifecycle_status === "classified" && !t.exclusion)) return false;
      if (filter === "excluded" && !t.exclusion) return false;
      if (!q) return true;
      const desc = (t.description || "").toLowerCase();
      const ref = (t.reference || "").toLowerCase();
      const date = (t.transaction_date || "").toLowerCase();
      const amtStr = String(t.amount_unsigned ?? "");
      const amtMatches = Number.isFinite(numericQ) && Math.abs(Number(t.amount_unsigned) - numericQ) < 0.005;
      return (
        desc.includes(q)
        || ref.includes(q)
        || date.includes(q)
        || amtStr.includes(q)
        || amtMatches
      );
    });
  }, [txns, filter, search]);

  useEffect(() => { if (focusIdx >= visible.length) setFocusIdx(Math.max(0, visible.length - 1)); }, [visible.length, focusIdx]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (drawer) {
        if (e.key === "Escape") setDrawer(null);
        return;
      }
      const k = (e.key || "").toLowerCase();
      if (k === "j" || e.key === "ArrowDown") { setFocusIdx(i => Math.min(visible.length - 1, i + 1)); e.preventDefault(); }
      else if (k === "k" || e.key === "ArrowUp") { setFocusIdx(i => Math.max(0, i - 1)); e.preventDefault(); }
      else if (k === "e") doExclude();
      else if (k === "c") doClassify();
      else if (k === "m") doMatchSelected();
      else if (k === "x") { setSelectedIds(new Set()); showToast("Selection cleared"); }
      else if (k === " " || k === "spacebar") {
        const t = visible[focusIdx];
        if (t) toggleSelected(t.id);
        e.preventDefault();
      }
      else if (k === "l") setDrawer("audit");
      else if (k === "b") setDrawer("lineage");
      else if (k === "1") setActiveStep("import");
      else if (k === "2") setActiveStep("review");
      else if (k === "3") setActiveStep("explain");
      else if (k === "4") setActiveStep("complete");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function toggleSelected(tid) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }

  async function doUnlock() {
    // Governed reopen — writes a rationale to the audit trail, then flips
    // the period back to `in_progress` so the Controller can correct the
    // opening/closing balances or activity, and re-certify.
    const reason = window.prompt(
      "Reopen this locked period.\n\n" +
      "Provide a rationale for the audit trail — this is required.\n" +
      "Example: \"Correct May 31 GL closing balance — journal 19369 back-dated after original close.\"",
    );
    if (!reason || !reason.trim()) {
      showToast("Rationale required to unlock");
      return;
    }
    try {
      setBusy(true);
      await api.unlockPeriod(id, reason.trim());
      showToast("Period reopened");
      await load();
    } catch (e) {
      showToast("Unlock failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  }

  async function doMatchSelected() {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) { showToast("Select at least one bank + one GL transaction"); return; }
    const all = data.transactions.filter(t => ids.includes(t.id));
    const bank_ids = all.filter(t => t.source === "bank").map(t => t.id);
    const gl_ids = all.filter(t => t.source === "gl").map(t => t.id);
    if (bank_ids.length === 0 || gl_ids.length === 0) {
      showToast("Match requires at least 1 bank and 1 GL transaction"); return;
    }
    if (all.some(t => t.lifecycle_status === "matched")) {
      showToast("One or more selected transactions are already matched"); return;
    }
    if (all.some(t => !!t.exclusion)) {
      showToast("Cannot match excluded transactions"); return;
    }
    // signed totals to give the Controller a heads-up
    const sum = (txns) => txns.reduce((s, t) => s + (t.dr_cr === "DR" ? t.amount_unsigned : -t.amount_unsigned), 0);
    const bankTotal = sum(all.filter(t => t.source === "bank"));
    const glTotal = sum(all.filter(t => t.source === "gl"));
    const rationale = window.prompt(
      `Create Match Group?\n\n${bank_ids.length} bank · ${gl_ids.length} GL\n` +
      `Bank net: ${bankTotal.toFixed(2)}  GL net: ${glTotal.toFixed(2)}  ` +
      `Diff: ${(Math.abs(bankTotal) - Math.abs(glTotal)).toFixed(2)}\n\n` +
      `Match rationale (required):`
    );
    if (!rationale) return;
    try {
      setBusy(true);
      await api.createMatchGroup({
        period_id: id,
        bank_transaction_ids: bank_ids,
        gl_transaction_ids: gl_ids,
        match_method: "manual_with_explanation",
        match_rationale: rationale,
        confidence: "high",
      });
      setSelectedIds(new Set());
      await load();
      showToast(`Matched ${bank_ids.length} bank + ${gl_ids.length} GL`);
    } catch (e) {
      showToast("Match failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  async function doUnmatch(matchGroupId) {
    const reason = window.prompt("Unmatch reason (required):");
    if (!reason) return;
    try {
      setBusy(true);
      await api.deleteMatchGroup(matchGroupId, reason);
      await load();
      showToast("Unmatched");
    } catch (e) {
      showToast("Unmatch failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  async function doClassify(overrideTxn) {
    const t = overrideTxn || visible[focusIdx];
    if (!t) return;
    setClassifyTargets([t]);
  }

  async function submitClassify({ category, reason }) {
    const rows = classifyTargets || [];
    if (!rows.length) return;
    let ok = 0, fail = 0;
    setBusy(true);
    try {
      for (const t of rows) {
        try { await api.reclassify(t.id, category, reason); ok++; }
        catch (e) { fail++; }
      }
      setClassifyTargets(null);
      await load();
      showToast(`Classified ${ok}${fail ? `, ${fail} failed` : ""}`);
    } finally { setBusy(false); }
  }

  async function doExclude(overrideTxn) {
    const t = overrideTxn || visible[focusIdx];
    if (!t) return;
    setExcludeTarget(t);
  }

  async function submitExclude({ type, reason }) {
    const t = excludeTarget;
    if (!t) return;
    try {
      setBusy(true);
      await api.exclude(t.id, type, reason);
      setExcludeTarget(null);
      await load();
      showToast("Excluded");
    } catch (e) {
      showToast("Failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  // Reverse a prior exclusion — puts the transaction back into the
  // certified population and re-triggers reconciliation server-side.
  async function doRestore(t) {
    if (!t?.id) return;
    const reason = window.prompt(
      "Restore this transaction from exclusion?\n\n"
      + "It will return to under_review and re-enter the certified "
      + "population. Provide a reason for the audit trail:",
      ""
    );
    if (!reason || !reason.trim()) return;
    try {
      setBusy(true);
      await api.restore(t.id, reason.trim());
      await load();
      showToast("Transaction restored. Variance recalculated.");
    } catch (e) {
      showToast("Restore failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  // Bulk: classify every selected row with the same category + reason.
  async function doBulkClassify(rows) {
    if (!rows?.length) return;
    setClassifyTargets(rows);
  }

  // Bulk unclassify — resets N transactions back to under_review in a
  // single request. Primary use case: preparing an existing GL import
  // for re-import (the replacement guard refuses to overwrite classified
  // rows, so they must be cleared first).
  async function doBulkUnclassify(rows) {
    if (!rows?.length) return;
    const reason = window.prompt(
      `Unclassify ${rows.length} selected transaction(s)?\n\n`
      + "They will return to Under Review (classification cleared, "
      + "lifecycle_status → under_review). Common reason: preparing "
      + "for a GL re-import.\n\nReason (required, applied to all):",
      "Preparing for GL re-import",
    );
    if (!reason || !reason.trim()) return;
    try {
      setBusy(true);
      const r = await api.bulkReclassify(
        rows.map(t => t.id),
        null,
        reason.trim(),
      );
      await load();
      const msg = r.failed
        ? `Unclassified ${r.reclassified} · ${r.failed} failed`
        : `Unclassified ${r.reclassified} transaction${r.reclassified === 1 ? "" : "s"}`;
      showToast(msg);
    } catch (e) {
      showToast("Bulk unclassify failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  // Bulk: exclude every selected row with the same type + reason.
  async function doBulkExclude(rows) {
    if (!rows?.length) return;
    const type = window.prompt(
      `Exclude ${rows.length} selected transaction(s). Type:\n` +
      `(duplicate_confirmed | wrong_period | import_error | controller_override | parser_artifact)`,
      "controller_override");
    if (!type) return;
    const reason = window.prompt("Reason (required, applied to all):");
    if (!reason?.trim()) { showToast("Reason required"); return; }
    let ok = 0, fail = 0;
    setBusy(true);
    try {
      for (const t of rows) {
        try { await api.exclude(t.id, type, reason.trim()); ok++; }
        catch (e) { fail++; }
      }
      await load();
      showToast(`Excluded ${ok}${fail ? `, ${fail} failed` : ""}`);
    } finally { setBusy(false); }
  }

  async function doUpload(file) {
    if (!file) return;
    try {
      setBusy(true);
      const r = await api.importFile(id, importType, file);
      showToast(`Imported ${r.row_count} ${importType} transactions`);
      await load();
    } catch (e) {
      // GOV-030 — if backend signals an active batch already exists, the
      // detail payload arrives as { code: IMPORT_REPLACEMENT_DECISION_REQUIRED, ... }.
      const detail = e?.response?.data?.detail;
      if (
        e?.response?.status === 409 &&
        detail &&
        typeof detail === "object" &&
        detail.code === "IMPORT_REPLACEMENT_DECISION_REQUIRED"
      ) {
        // Stash the file + payload; the modal will resubmit with the controller's decision.
        setPendingImportFile(file);
        setReplacementPrompt(detail);
        return;
      }
      const msg = typeof detail === "string" ? detail : detail?.message || e.message;
      showToast("Import failed: " + msg);
    } finally { setBusy(false); }
  }

  async function doImportWithDecision({ replacement_decision, replacement_reason, superseded_batch_id, supersede_mode }) {
    if (!pendingImportFile) {
      setReplacementPrompt(null);
      return;
    }
    try {
      setBusy(true);
      const r = await api.importFile(id, importType, pendingImportFile, {
        replacement_decision,
        replacement_reason,
        superseded_batch_id,
        supersede_mode,
      });
      const verb = replacement_decision === "replace_existing" ? "Replaced" : "Appended";
      showToast(`${verb} · ${r.row_count} ${importType} transactions`);
      setReplacementPrompt(null);
      setPendingImportFile(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || e.message;
      showToast("Import failed: " + msg);
    } finally { setBusy(false); }
  }

  function cancelReplacementPrompt() {
    setReplacementPrompt(null);
    setPendingImportFile(null);
    showToast("Import cancelled");
  }

  async function doDetectDuplicates() {
    try { setBusy(true); const r = await api.detectDuplicates(id); showToast(`Detected ${r.clusters_created} cluster(s)`); await load(); }
    catch (e) { showToast("Failed: " + e.message); }
    finally { setBusy(false); }
  }

  async function doCertify(source) {
    try { setBusy(true); await api.certifyPopulation(id, source); showToast(`${source.toUpperCase()} population certified`); await load(); }
    catch (e) { showToast("Failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  }

  async function doCompute() {
    try { setBusy(true); await api.computeReconciliation(id); showToast("Reconciliation computed"); await load(); }
    catch (e) { showToast("Failed: " + e.message); }
    finally { setBusy(false); }
  }

  async function doComplete(lockAlso) {
    const note = window.prompt("Completion note (optional):");
    let variance_explanation = period?.variance_explanation;
    const snap = data?.latest_reconciliation_snapshot;
    if (snap && (Math.abs(snap.bank_variance) > 0.01 || Math.abs(snap.gl_variance) > 0.01) && !variance_explanation) {
      variance_explanation = window.prompt("Variance explanation (required because variance > 0):");
      if (!variance_explanation) { showToast("Variance explanation required"); return; }
    }
    try { setBusy(true); await api.complete(id, { variance_explanation, completion_note: note, lock_immediately: !!lockAlso }); showToast("Completed"); await load(); }
    catch (e) { showToast("Blocked: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  }

  // Lock a period that was already Completed but not yet Locked.
  // Certification Package generation requires status="locked" — this is
  // the missing step when the user clicked "Complete Reconciliation"
  // instead of "Complete & Lock".
  async function doLock() {
    if (!window.confirm(
      "Lock this period?\n\n"
      + "Locking is required before the Certification Package can be marked "
      + "CERTIFIED. Locked periods can only be edited via supersession or "
      + "an explicit unlock (audit-trailed)."
    )) return;
    try {
      setBusy(true);
      await api.lockPeriod(id);
      showToast("Period locked. Re-generate the Certification Package.");
      await load();
    } catch (e) {
      showToast("Lock failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  if (loading) return <div className="loading" data-testid="ws-loading">Loading workspace…</div>;
  if (!period) return <div className="loading">Period not found.</div>;

  const snap = data.latest_reconciliation_snapshot;
  const bankSnap = data.latest_bank_population_snapshot;
  const glSnap = data.latest_gl_population_snapshot;
  const posted = data.latest_posted_population_snapshot;
  const bankLineage = data.latest_bank_balance_lineage;
  const glLineage = data.latest_gl_balance_lineage;
  const blockers = snap?.completion_blockers || [];

  const stepProgress = {
    import: data.import_batches.length > 0,
    review: txns.length > 0 && !txns.some(t => ["imported", "under_review"].includes(t.lifecycle_status) && !t.exclusion),
    explain: !!snap && (Math.abs(snap.bank_variance) < 0.01 && Math.abs(snap.gl_variance) < 0.01 || !!period.variance_explanation),
    complete: period.status === "complete" || period.status === "locked",
  };

  return (
    <div className="workspace-shell" data-testid="workspace-page">
      {/* Header */}
      <div className="workspace-header">
        <div>
          <Link to="/" className="back" data-testid="ws-back">← Hub</Link>
          <span className="heading h2" data-testid="ws-title">{data.account.name}</span>
          <span className="muted" style={{ marginLeft: 10, fontSize: 14 }}>· {period.period_label}</span>
          <span className={"tag " + (period.status === "locked" ? "ok" : period.status === "complete" ? "ok" : "attn")}
                style={{ marginLeft: 12 }} data-testid="ws-status">
            {period.status}
          </span>
          {/* Phase G-B · governance verdict strip */}
          {cert && <span style={{ marginLeft: 8 }}>
            <VerdictBadge verdict={cert.verdict} dataTestId="ws-attested-verdict" compact />
          </span>}
          {!cert && period.status === "locked" && (
            <span style={{ marginLeft: 8 }}>
              <UnattestedBadge dataTestId="ws-unattested" />
            </span>
          )}
          {drift && drift.drifted && (
            <DriftBadge drift={drift} dataTestId="ws-drift" />
          )}
          <span style={{ marginLeft: 8 }}>
            <BankCertBadge cert={bankCert} onClick={() => setDrawer("bankcert")} />
          </span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(period.status === "complete" || period.status === "locked") && (
            <button
              className="btn primary btn-sm"
              onClick={() => setShowCertify(true)}
              data-testid="ws-certify-btn">
              {cert ? "Re-certify period" : "Certify period"}
            </button>
          )}
          {period.status === "locked" && (
            <button
              className="btn ghost btn-sm"
              onClick={doUnlock}
              disabled={busy}
              title="Reopen a locked period to correct balances or activity. A rationale is written to the audit trail."
              data-testid="ws-unlock-btn">
              Unlock
            </button>
          )}
          {period.status === "complete" && (
            <button
              className="btn ghost btn-sm"
              onClick={doUnlock}
              disabled={busy}
              title="Reopen a completed (but unlocked) period to edit balances or activity. A rationale is written to the audit trail."
              data-testid="ws-reopen-btn">
              Reopen for Editing
            </button>
          )}
          <button className="btn ghost btn-sm" onClick={() => setDrawer("recognition")} data-testid="ws-open-recognition">Recognition</button>
          <button className="btn ghost btn-sm" onClick={() => setDrawer("lineage")} data-testid="ws-open-lineage">Balance Lineage</button>
          <button className="btn ghost btn-sm" onClick={() => setDrawer("audit")} data-testid="ws-open-audit" title="Open the activity log for this period">Activity Log</button>
          <button className="btn ghost btn-sm" onClick={() => setDrawer("history")} data-testid="ws-open-history">Certification History</button>
        </div>
      </div>

      {/* Stepper */}
      <div className="stepper" data-testid="stepper">
        {STEPS.map((s, i) => {
          const done = stepProgress[s.key] && s.key !== activeStep;
          // Subtle "attention needed" dot: the Statement Targets (bank +
          // GL closing balance) live on the Explain step; if the Controller
          // has not entered them yet the whole reconciliation is anchored
          // at zero and every variance number is misleading.
          const explainNeedsTargets = s.key === "explain"
            && period
            && !done
            && period.status !== "locked"
            && period.status !== "complete"
            && (period.closing_balance == null || period.gl_closing_balance == null);
          return (
            <div key={s.key}
                 className={"step" + (s.key === activeStep ? " active" : "") + (done ? " done" : "")}
                 onClick={() => setActiveStep(s.key)}
                 data-testid={`step-${s.key}`}>
              <span className="num">{done ? "✓" : i + 1}</span>
              <span className="label">{s.label.replace(/^\d+\s*·\s*/, "")}</span>
              {explainNeedsTargets && (
                <span
                  className="step-alert-dot"
                  title="Statement targets not entered yet — bank and GL closing balances are needed for a meaningful variance."
                  data-testid="step-explain-targets-pending"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div className="ws-body">
        <div className="ws-work">
          {activeStep === "import" && (
            <ImportStep period={period} importBatches={data.import_batches}
                        importType={importType} setImportType={setImportType}
                        onPick={() => fileInputRef.current?.click()} fileInputRef={fileInputRef}
                        onFile={doUpload}
                        showToast={showToast}
                        onChanged={() => { load(); setCertBump(b => b + 1); }} />
          )}
          {activeStep === "review" && (
            <ReviewStep visible={visible} txns={txns} focusIdx={focusIdx} setFocusIdx={setFocusIdx}
                        filter={filter} setFilter={setFilter}
                        search={search} setSearch={setSearch}
                        clusters={data.duplicate_clusters}
                        selectedIds={selectedIds} toggleSelected={toggleSelected}
                        onMatch={doMatchSelected} onUnmatch={doUnmatch}
                        matchGroups={data.match_groups}
                        onClassify={doClassify} onExclude={doExclude} onRestore={doRestore}
                        onBulkClassify={doBulkClassify} onBulkExclude={doBulkExclude}
                        onBulkUnclassify={doBulkUnclassify}
                        onDetectDuplicates={doDetectDuplicates} />
          )}
          {activeStep === "explain" && (
            <ExplainStep periodId={id} period={period} snap={snap}
                         bankLineage={bankLineage} glLineage={glLineage}
                         journalEntries={data.journal_entries}
                         onCompute={doCompute} onRefresh={load} showToast={showToast} />
          )}
          {activeStep === "complete" && (
            <>
              <CompleteStep period={period} snap={snap} blockers={blockers}
                            posted={posted}
                            onComplete={doComplete}
                            onLock={doLock}
                            onCompute={doCompute} />
              {/* RIE-1 · Disclose bank variance as Reconciling Item */}
              <ReconcilingItemsPanel
                periodId={id}
                period={period}
                bankVariance={snap?.bank_variance}
                onChanged={load} />
            </>
          )}
        </div>

        <div className="ws-snap">
          <SnapshotPane snap={snap} bankSnap={bankSnap} glSnap={glSnap} posted={posted}
                        importBatches={data.import_batches}
                        period={period}
                        tab={snapTab} setTab={setSnapTab}
                        excludedCount={txns.filter(t => !!t.exclusion).length}
                        onJumpToExcluded={() => { setActiveStep("review"); setFilter("excluded"); }}
                        onCertify={doCertify} onCompute={doCompute} busy={busy} />
          <BlockersCard blockers={blockers} />
        </div>
      </div>

      {/* Drawers */}
      {drawer && (
        <>
          <div className="drawer-backdrop" onClick={() => setDrawer(null)} />
          <div className="drawer" data-testid={`drawer-${drawer}`}>
            <div className="drawer-head">
              <div className="heading h3">{
                drawer === "audit" ? "Activity Log" :
                drawer === "lineage" ? "Balance Lineage" :
                drawer === "recognition" ? "Recognition Engine" :
                drawer === "history" ? "Certification History" :
                drawer === "bankcert" ? "Bank Statement Certification" : ""
              }</div>
              <button className="btn ghost btn-sm" onClick={() => setDrawer(null)} data-testid="drawer-close">Close (Esc)</button>
            </div>
            <div className="drawer-body">
              {drawer === "audit" && <AuditDrawer logs={data.audit_logs} />}
              {drawer === "lineage" && <LineageDrawer bankLineage={bankLineage} glLineage={glLineage} posted={posted} period={period} />}
              {drawer === "recognition" && <RecognitionPanel periodId={id} periodStatus={period.status} onChanged={load} />}
              {drawer === "bankcert" && <BankCertDrawer periodId={id} bankBatches={data.import_batches.filter(b => b.import_type === "bank")} showToast={showToast} onChanged={() => setCertBump(b => b + 1)} />}
            </div>
          </div>
        </>
      )}

      {toast && (
        <div
          className={`toast ${toast.kind === "error" ? "toast-error" : ""}`}
          data-testid="toast"
          role={toast.kind === "error" ? "alert" : "status"}
        >
          <span>{toast.msg}</span>
          <button
            type="button"
            className="toast-close"
            onClick={dismissToast}
            data-testid="toast-dismiss"
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* Phase G-B · Certify modal */}
      {showCertify && (
        <CertifyDialog
          periodId={id}
          onClose={() => setShowCertify(false)}
          onCertified={() => setCertBump(b => b + 1)}
        />
      )}

      {/* GOV-030 · Import Replacement Prompt */}
      {replacementPrompt && (
        <ImportReplacementPrompt
          payload={replacementPrompt}
          onDecision={doImportWithDecision}
          onCancel={cancelReplacementPrompt}
        />
      )}

      {/* Exclude transaction dialog */}
      {excludeTarget && (
        <ExcludeDialog
          transaction={excludeTarget}
          onConfirm={submitExclude}
          onCancel={() => setExcludeTarget(null)}
        />
      )}

      {/* Classify dialog (handles single + bulk via array of transactions) */}
      {classifyTargets && classifyTargets.length > 0 && (
        <ClassifyDialog
          transactions={classifyTargets}
          onConfirm={submitClassify}
          onCancel={() => setClassifyTargets(null)}
        />
      )}

      <KeyboardHint />
    </div>
  );
}

// ── Step components ────────────────────────────────────────────────────────

function ImportStep({ period, importBatches, importType, setImportType, onPick, fileInputRef, onFile, showToast, onChanged }) {
  const pdfInputRef = React.useRef();
  const [importingPdf, setImportingPdf] = React.useState(false);
  async function onBankPdfFile(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      showToast?.("Only PDF files are accepted for bank PDF import.");
      return;
    }
    try {
      setImportingPdf(true);
      showToast?.("Uploading and extracting bank PDF… this takes ~1-3 minutes.");
      const d = await api.importBankFromPdf(period.id, file);
      showToast?.(
        `Bank imported from PDF: ${d.row_count} txns · ${d.statement_period_start} → ${d.statement_period_end} · auto-certified.`
      );
      onChanged?.();
    } catch (e) {
      showToast?.("PDF bank import failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setImportingPdf(false);
    }
  }
  return (
    <div className="card" data-testid="step-import-pane">
      <div className="heading h2">Import — Source Files</div>
      <div className="muted" style={{ marginBottom: 18, marginTop: 4, fontSize: 13 }}>
        Drop a bank statement or GL export to start. Bank imports now support PDF (recommended) — the statement PDF is the source of truth, so no Excel/PDF variance can occur.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
        <label style={{ fontSize: 13 }}>Type:</label>
        <select value={importType} onChange={e => setImportType(e.target.value)} data-testid="import-type-select">
          <option value="bank">Bank</option>
          <option value="gl">General Ledger</option>
        </select>
        {importType === "bank" ? (
          <>
            <button className="btn primary"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={importingPdf}
                    data-testid="import-bank-pdf-btn">
              {importingPdf ? "Importing from PDF…" : "Import Bank from PDF"}
            </button>
            <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
                   onChange={e => { onBankPdfFile(e.target.files?.[0]); e.target.value = ""; }} />
            <button className="btn ghost btn-sm" onClick={onPick} data-testid="import-pick-btn" style={{ marginLeft: 4 }}>
              Legacy: Excel/CSV
            </button>
          </>
        ) : (
          <button className="btn primary" onClick={onPick} data-testid="import-pick-btn">Upload File…</button>
        )}
        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
               onChange={e => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      <div className="heading h3" style={{ marginTop: 24, marginBottom: 10 }}>Imported batches</div>
      {importBatches.length === 0 && <div className="empty">No imports yet.</div>}
      {importBatches.map(b => (
        <div key={b.id} className="card" style={{ marginBottom: 10, padding: "12px 14px" }} data-testid={`batch-${b.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{b.file_name}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                {b.import_type.toUpperCase()} · {b.source_row_count} rows · imported {fmt.dateTime(b.created_at)}
              </div>
            </div>
            <div className="mono" style={{ textAlign: "right", fontSize: 12 }}>
              <div>DR ${fmt.currency(b.source_total_debits)}</div>
              <div className="muted">CR ${fmt.currency(b.source_total_credits)}</div>
            </div>
          </div>
          {b.parse_warning && (
            <div
              data-testid={`batch-${b.id}-parse-warning`}
              style={{
                marginTop: 10,
                padding: "10px 12px",
                background: "#fff8e6",
                border: "1px solid #d9a80e",
                borderRadius: 6,
                fontSize: 12.5,
                color: "#6b4c00",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 3 }}>⚠ Import sign-detection warning</div>
              {b.parse_warning}
            </div>
          )}
          {b.import_type === "bank" && (
            <BankStatementCertificationPanel
              batchId={b.id}
              showToast={showToast}
              onChanged={onChanged}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ReviewStep({ visible, txns, focusIdx, setFocusIdx, filter, setFilter,
                     search, setSearch,
                     clusters, selectedIds, toggleSelected, onMatch, onUnmatch, matchGroups,
                     onClassify, onExclude, onRestore, onBulkClassify, onBulkExclude, onBulkUnclassify, onDetectDuplicates }) {
  // Track the last-checked row index so shift-click can select a range.
  const lastCheckedIdxRef = useRef(null);
  const counts = {
    under_review: txns.filter(t => ["imported", "under_review"].includes(t.lifecycle_status) && !t.exclusion).length,
    matched: txns.filter(t => t.lifecycle_status === "matched").length,
    classified: txns.filter(t => t.lifecycle_status === "classified" && !t.exclusion).length,
    excluded: txns.filter(t => !!t.exclusion).length,
  };
  // Selection summary (only meaningful in under_review mode)
  const selectedRows = visible.filter(t => selectedIds.has(t.id));
  const selBank = selectedRows.filter(t => t.source === "bank");
  const selGL = selectedRows.filter(t => t.source === "gl");
  const matchReady = selBank.length >= 1 && selGL.length >= 1;
  const mgById = (matchGroups || []).reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
  return (
    <div data-testid="step-review-pane">
      <div className="workpane-header">
        <div>
          <div className="heading h2">Review</div>
          <div className="muted" style={{ fontSize: 12.5 }}>Classify, match, or exclude each row.</div>
        </div>
        <div className="filter-bar" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="filter-select">
            <option value="under_review">Under review ({counts.under_review})</option>
            <option value="matched">Matched ({counts.matched})</option>
            <option value="classified">Classified ({counts.classified})</option>
            <option value="excluded">Excluded ({counts.excluded})</option>
            <option value="all">All ({txns.length})</option>
          </select>
          <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200, maxWidth: 380 }}>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search description, amount, date, reference…"
              data-testid="review-search-input"
              style={{
                width: "100%",
                padding: "6px 28px 6px 10px",
                border: "1px solid var(--line-strong)",
                borderRadius: 6,
                fontSize: 13,
                background: "var(--surface)",
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="review-search-clear"
                title="Clear search"
                style={{
                  position: "absolute",
                  right: 6, top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 15,
                  color: "var(--ink-mute)",
                  padding: "0 4px",
                  lineHeight: 1,
                }}
              >×</button>
            )}
          </div>
          {search && (
            <span
              className="muted"
              data-testid="review-search-count"
              style={{ fontSize: 12 }}
            >
              {visible.length} match{visible.length === 1 ? "" : "es"}
            </span>
          )}
          <button className="btn btn-sm" onClick={onDetectDuplicates} data-testid="detect-duplicates-btn">Detect Duplicates</button>
        </div>
      </div>

      {selectedRows.length > 0 && (
        <div style={{
          background: "var(--surface-elev)", border: "1px solid var(--line-strong)",
          borderRadius: 8, padding: "10px 14px", marginBottom: 10,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }} data-testid="selection-bar">
          <div style={{ fontSize: 13 }}>
            <strong>{selectedRows.length}</strong> selected · {selBank.length} bank · {selGL.length} GL
            {!matchReady && <span className="muted" style={{ marginLeft: 8 }}>(need ≥1 bank + ≥1 GL)</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn primary btn-sm" disabled={!matchReady}
                    onClick={onMatch} data-testid="create-match-btn">
              Create Match Group  <kbd style={{ marginLeft: 6 }}>M</kbd>
            </button>
            <button className="btn btn-sm" disabled={selectedRows.length === 0}
                    onClick={() => onBulkClassify(selectedRows)}
                    data-testid="bulk-classify-btn">
              Classify Selected
            </button>
            <button className="btn btn-sm" disabled={selectedRows.length === 0}
                    onClick={() => onBulkUnclassify(selectedRows)}
                    data-testid="bulk-unclassify-btn"
                    title="Reset classifications back to Under Review — needed before re-importing a GL batch that has classified rows">
              Unclassify Selected
            </button>
            <button className="btn btn-sm" disabled={selectedRows.length === 0}
                    onClick={() => onBulkExclude(selectedRows)}
                    data-testid="bulk-exclude-btn">
              Exclude Selected
            </button>
            <button className="btn btn-sm" onClick={() => {
              selectedRows.forEach(t => toggleSelected(t.id));
            }} data-testid="clear-selection-btn">Clear</button>
          </div>
        </div>
      )}

      {clusters.filter(c => c.status === "pending_review").length > 0 && (
        <div className="blockers-card" style={{ marginBottom: 14 }} data-testid="workspace-dup-blockers">
          <h4>{clusters.filter(c => c.status === "pending_review").length} duplicate cluster(s) pending controller review</h4>
          <ul>
            {clusters.filter(c => c.status === "pending_review").slice(0, 3).map(c => (
              <li key={c.id}>
                <Link
                  to={`/registers/duplicates?cluster=${c.id}`}
                  data-testid={`workspace-dup-link-${c.id}`}
                  style={{ color: "var(--ink)", textDecoration: "underline" }}
                >
                  {c.transaction_ids.length} transactions · {c.confidence_band} · score {c.score.toFixed(2)}
                </Link>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <Link
              to="/registers/duplicates"
              data-testid="workspace-dup-open-register"
              style={{ color: "var(--ink-mute)" }}
            >
              Open Duplicate Register →
            </Link>
          </div>
        </div>
      )}

      <div className="txn-grid" data-testid="txn-grid">
        <div className="txn-grid-header">
          <div></div>
          <div>Src</div>
          <div>Date</div>
          <div>Ref</div>
          <div>Description</div>
          <div style={{ textAlign: "right" }}>Amount</div>
          <div>Status</div>
          <div style={{ textAlign: "right" }}>Actions</div>
        </div>
        {/* Toolbar — Select all in view + shift-click hint */}
        <div style={{ display: "flex", alignItems: "center", gap: 12,
                      padding: "6px 12px", background: "#f8f9fb",
                      borderBottom: "1px solid #e5e7eb", fontSize: 12 }}
             data-testid="txn-list-toolbar">
          {(() => {
            const eligible = visible.filter(t => t.lifecycle_status !== "matched" && !t.exclusion);
            const eligibleIds = eligible.map(t => t.id);
            const allSelected = eligible.length > 0 && eligibleIds.every(id => selectedIds.has(id));
            const someSelected = eligibleIds.some(id => selectedIds.has(id));
            return (
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox"
                       data-testid="select-all-visible"
                       checked={allSelected}
                       ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                       onChange={() => {
                         if (allSelected) {
                           eligibleIds.forEach(id => { if (selectedIds.has(id)) toggleSelected(id); });
                         } else {
                           eligibleIds.forEach(id => { if (!selectedIds.has(id)) toggleSelected(id); });
                         }
                       }} />
                <span>Select all {eligible.length} eligible row(s) in view</span>
              </label>
            );
          })()}
          <span style={{ color: "#6b7280" }}>
            Tip: click a checkbox, then <b>shift-click</b> another checkbox to select the range.
          </span>
        </div>
        {visible.length === 0 && <div className="empty" style={{ borderRadius: 0, border: "none" }}>Nothing matches this filter.</div>}
        {visible.map((t, i) => {
          const archived = !!t.exclusion;
          const matched = t.lifecycle_status === "matched";
          const selected = selectedIds.has(t.id);
          return (
            <div key={t.id}
                 className={"txn-row" + (i === focusIdx ? " focus" : "") + (matched ? " matched" : "") + (archived ? " archived" : "") + (selected ? " selected" : "")}
                 onClick={() => setFocusIdx(i)}
                 data-testid={`txn-${t.id}`}>
              <div onClick={(e) => {
                     e.stopPropagation();
                     if (matched || archived) return;
                     if (e.shiftKey && lastCheckedIdxRef.current !== null) {
                       // Range select from last-clicked to i
                       const start = Math.min(lastCheckedIdxRef.current, i);
                       const end = Math.max(lastCheckedIdxRef.current, i);
                       const target = !selected;
                       for (let k = start; k <= end; k++) {
                         const row = visible[k];
                         if (!row || row.lifecycle_status === "matched" || row.exclusion) continue;
                         if (selectedIds.has(row.id) !== target) toggleSelected(row.id);
                       }
                     } else {
                       toggleSelected(t.id);
                     }
                     lastCheckedIdxRef.current = i;
                   }}
                   style={{ cursor: matched || archived ? "default" : "pointer", textAlign: "center" }}
                   data-testid={`select-${t.id}`}>
                <input type="checkbox" checked={selected} disabled={matched || archived}
                       readOnly
                       onClick={(e) => e.stopPropagation()} />
              </div>
              <div className={"txn-source " + t.source}>{t.source.toUpperCase()}</div>
              <div>{fmt.date(t.transaction_date)}</div>
              <div
                className="mono muted"
                title={t.reference || ""}
                style={{
                  fontSize: 10,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t.reference || "—"}
              </div>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.description}</div>
              <div className={"txn-amount " + (t.dr_cr === "CR" ? "muted" : "")}>
                {t.dr_cr === "CR" ? `(${fmt.currency(t.amount_unsigned)})` : fmt.currency(t.amount_unsigned)}
              </div>
              <div className="txn-status">
                {archived ? <span className="tag idle">excluded</span>
                  : matched ? <span className="tag ok" title={mgById[t.match_group_id]?.match_rationale || ""}>matched</span>
                  : t.classification ? <span className="tag attn">{t.classification}</span>
                  : <span className="muted">—</span>}
              </div>
              <div className="txn-actions">
                {matched ? (
                  <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); onUnmatch(t.match_group_id); }}
                          data-testid={`btn-unmatch-${t.id}`}>Unmatch</button>
                ) : archived ? (
                  <button className="btn btn-sm"
                          onClick={(e) => { e.stopPropagation(); setFocusIdx(i); onRestore(t); }}
                          data-testid={`btn-restore-${t.id}`}
                          title="Reverse the exclusion and put this transaction back into the certified population">
                    Restore
                  </button>
                ) : (
                  <>
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setFocusIdx(i); onClassify(t); }} data-testid={`btn-classify-${t.id}`}>Classify</button>
                    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setFocusIdx(i); onExclude(t); }} data-testid={`btn-exclude-${t.id}`}>Exclude</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatementTargetsCard({ period, onSaved, showToast }) {
  const isLocked = period.status === "complete" || period.status === "locked";
  const [bank, setBank] = React.useState(
    period.closing_balance != null ? String(period.closing_balance) : ""
  );
  const [gl, setGl] = React.useState(
    period.gl_closing_balance != null ? String(period.gl_closing_balance) : ""
  );
  const [bankOpen, setBankOpen] = React.useState(
    period.opening_balance != null ? String(period.opening_balance) : ""
  );
  const [glOpen, setGlOpen] = React.useState(
    period.gl_opening_balance != null ? String(period.gl_opening_balance) : ""
  );
  const [saving, setSaving] = React.useState(false);
  const [showOpenings, setShowOpenings] = React.useState(false);

  // Refresh local state when the period switches or is re-fetched.
  React.useEffect(() => {
    setBank(period.closing_balance != null ? String(period.closing_balance) : "");
    setGl(period.gl_closing_balance != null ? String(period.gl_closing_balance) : "");
    setBankOpen(period.opening_balance != null ? String(period.opening_balance) : "");
    setGlOpen(period.gl_opening_balance != null ? String(period.gl_opening_balance) : "");
  }, [period.id, period.closing_balance, period.gl_closing_balance,
      period.opening_balance, period.gl_opening_balance]);

  const parseNumber = (s) => {
    if (s === "" || s == null) return null;
    const n = Number(String(s).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const dirty =
    parseNumber(bank) !== (period.closing_balance ?? null) ||
    parseNumber(gl) !== (period.gl_closing_balance ?? null) ||
    parseNumber(bankOpen) !== (period.opening_balance ?? null) ||
    parseNumber(glOpen) !== (period.gl_opening_balance ?? null);

  async function save() {
    const bv = parseNumber(bank);
    const gv = parseNumber(gl);
    if (bv == null || gv == null) {
      showToast("Enter numeric values for both closing balances.");
      return;
    }
    // Openings are optional — only send when provided AND changed.
    const bopen = parseNumber(bankOpen);
    const gopen = parseNumber(glOpen);
    const payload = { closing_balance: bv, gl_closing_balance: gv };
    if (bopen != null && bopen !== (period.opening_balance ?? null)) {
      payload.opening_balance = bopen;
    }
    if (gopen != null && gopen !== (period.gl_opening_balance ?? null)) {
      payload.gl_opening_balance = gopen;
    }
    try {
      setSaving(true);
      await api.editBalances(period.id, payload);
      showToast("Statement targets saved. Recompute reconciliation to refresh the variance.");
      if (onSaved) await onSaved();
    } catch (e) {
      showToast("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" data-testid="statement-targets-card" style={{ marginBottom: 14 }}>
      <div className="heading h3" style={{ marginBottom: 4 }}>Statement Targets</div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Enter the certified target from the source-of-truth documents.  The
        reconciliation engine compares these against the imported activity to
        surface variance.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
          <span className="muted" style={{ letterSpacing: ".02em" }}>
            Bank statement closing balance
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={bank}
            onChange={e => setBank(e.target.value)}
            placeholder="0.00"
            disabled={isLocked || saving}
            data-testid="bank-closing-balance-input"
            style={{
              fontFamily: "var(--mono)", fontSize: 15, padding: "8px 10px",
              border: "1px solid var(--line-strong)", borderRadius: 6,
              background: isLocked ? "var(--surface-elev)" : "var(--surface)",
            }}
          />
          <span className="muted" style={{ fontSize: 11 }}>
            From the bank statement PDF or online banking.
          </span>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
          <span className="muted" style={{ letterSpacing: ".02em" }}>
            GL closing balance
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={gl}
            onChange={e => setGl(e.target.value)}
            placeholder="0.00"
            disabled={isLocked || saving}
            data-testid="gl-closing-balance-input"
            style={{
              fontFamily: "var(--mono)", fontSize: 15, padding: "8px 10px",
              border: "1px solid var(--line-strong)", borderRadius: 6,
              background: isLocked ? "var(--surface-elev)" : "var(--surface)",
            }}
          />
          <span className="muted" style={{ fontSize: 11 }}>
            From the trial balance for this account at period-end.
          </span>
        </label>
      </div>

      {!isLocked && (
        <div style={{ marginTop: 12, borderTop: "1px dashed var(--line)", paddingTop: 10 }}>
          <button
            className="btn-link"
            data-testid="toggle-opening-balances"
            onClick={() => setShowOpenings(v => !v)}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 12, color: "var(--ink-mute)", textDecoration: "underline dotted",
            }}
          >
            {showOpenings ? "▼" : "▶"} Advanced — override opening balances
          </button>
          {showOpenings && (
            <>
              <div className="muted" style={{ fontSize: 11, margin: "6px 0 10px" }}>
                Openings roll forward automatically from the prior period's certified
                closing. Override ONLY when the prior period was locked with a value
                that doesn't match the trial balance (rare; audit-trailed).
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
                  <span className="muted" style={{ letterSpacing: ".02em" }}>
                    Bank statement opening balance
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bankOpen}
                    onChange={e => setBankOpen(e.target.value)}
                    placeholder="0.00"
                    disabled={isLocked || saving}
                    data-testid="bank-opening-balance-input"
                    style={{
                      fontFamily: "var(--mono)", fontSize: 15, padding: "8px 10px",
                      border: "1px solid var(--line-strong)", borderRadius: 6,
                      background: "var(--surface)",
                    }}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
                  <span className="muted" style={{ letterSpacing: ".02em" }}>
                    GL opening balance
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={glOpen}
                    onChange={e => setGlOpen(e.target.value)}
                    placeholder="0.00"
                    disabled={isLocked || saving}
                    data-testid="gl-opening-balance-input"
                    style={{
                      fontFamily: "var(--mono)", fontSize: 15, padding: "8px 10px",
                      border: "1px solid var(--line-strong)", borderRadius: 6,
                      background: "var(--surface)",
                    }}
                  />
                </label>
              </div>
            </>
          )}
        </div>
      )}
      {!isLocked && (
        <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            className="btn primary btn-sm"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="save-statement-targets-btn"
          >
            {saving ? "Saving…" : "Save Statement Targets"}
          </button>
        </div>
      )}
      {isLocked && (
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          Period is locked — balances are read-only.
        </div>
      )}
    </div>
  );
}

function ExplainStep({ periodId, period, snap, bankLineage, glLineage, journalEntries, onCompute, onRefresh, showToast }) {
  const [contribs, setContribs] = useState(null);
  const [explanation, setExplanation] = useState(period.variance_explanation || "");

  useEffect(() => {
    api.investigate(periodId).then(setContribs).catch(() => {});
  }, [periodId]);

  return (
    <div data-testid="step-explain-pane">
      <StatementTargetsCard period={period} onSaved={onRefresh} showToast={showToast} />
      <div className="workpane-header">
        <div>
          <div className="heading h2">Explain — Variance Investigation</div>
          <div className="muted" style={{ fontSize: 12.5 }}>The balance lineage and the ranked contributors. You decide.</div>
        </div>
        <button className="btn btn-sm" onClick={onCompute} data-testid="recompute-btn">Recompute Reconciliation</button>
      </div>

      {!snap && (
        <div className="empty">Click "Recompute Reconciliation" to generate the balance lineage and variance.</div>
      )}

      {bankLineage && <BalanceLineage title="Bank Lineage" lineage={bankLineage} />}
      {glLineage && <BalanceLineage title="GL Lineage" lineage={glLineage} />}

      {contribs && contribs.contributors?.length > 0 && (
        <div className="card" data-testid="contributors-card">
          <div className="heading h3" style={{ marginBottom: 10 }}>Ranked Contributors</div>
          {contribs.contributors.slice(0, 15).map((c, i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--line)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <span className="mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>{c.source}</span>
                  <div style={{ marginTop: 2, fontSize: 13 }}>{c.evidence?.summary}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                  <div className="mono" style={{ fontWeight: 600 }}>${fmt.currency(c.dollar_impact)}</div>
                  {c.source === "excluded_transaction" && c.evidence?.entity_id && (
                    <button
                      className="btn-link"
                      data-testid={`contrib-restore-${c.evidence.entity_id}`}
                      onClick={async () => {
                        const reason = window.prompt(
                          "Restore this transaction from exclusion?\n\n"
                          + "It will return to under_review and re-enter the certified population. "
                          + "Provide a reason for the audit trail:",
                          ""
                        );
                        if (!reason || !reason.trim()) return;
                        try {
                          await api.restore(c.evidence.entity_id, reason.trim());
                          await api.computeReconciliation(periodId);
                          showToast("Transaction restored. Variance recalculated.");
                          // Force a refresh of the contribs panel
                          window.location.reload();
                        } catch (e) {
                          showToast(e?.response?.data?.detail || "Restore failed");
                        }
                      }}
                      style={{
                        background: "none", border: "none",
                        color: "#1e5d34", fontSize: 12, cursor: "pointer",
                        textDecoration: "underline", padding: 0,
                      }}
                      title="Reverse the exclusion and put this transaction back into the certified population"
                    >
                      restore
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop: 14 }} data-testid="explanation-card">
        <div className="heading h3" style={{ marginBottom: 8 }}>Controller Variance Explanation</div>
        <textarea
          value={explanation}
          onChange={e => setExplanation(e.target.value)}
          placeholder="Document why this variance exists, what evidence supports the explanation, and whether any action is required."
          rows={5}
          style={{ width: "100%", fontFamily: "var(--sans)", fontSize: 13, padding: 10, border: "1px solid var(--line-strong)", borderRadius: 6 }}
          data-testid="explanation-textarea"
        />
        <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn primary" onClick={async () => {
            try {
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/periods/${periodId}/balances`, {
                method: "PATCH", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ notes: period.notes || "" }),
              });
              await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/periods/${periodId}`, {});
              // call backend to update variance_explanation via balances patch endpoint (re-uses edit_balances)
              await api.computeReconciliation(periodId);
              showToast("Explanation noted (will save on Complete)");
            } catch (e) { showToast("Save failed"); }
            // Save into local UI state; persistence happens at completion since variance_explanation flows through the complete endpoint
          }} data-testid="save-explanation-btn">Use on Completion</button>
        </div>
      </div>
    </div>
  );
}

function CompleteStep({ period, snap, blockers, posted, onComplete, onLock }) {
  const isComplete = period.status === "complete" || period.status === "locked";
  const isLocked = period.status === "locked";
  return (
    <div data-testid="step-complete-pane">
      <div className="card">
        <div className="heading h2">Complete & Sign Off</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 18 }}>
          Signing the period freezes the population snapshot and emits the Certification Package.
        </div>

        {!snap && <div className="empty">Compute the reconciliation first (Step 3).</div>}
        {snap && (
          <div style={{ background: "var(--surface-elev)", padding: 14, borderRadius: 6, marginBottom: 16 }}>
            <div className="snap-line"><span>Bank variance</span><span className={"v " + (Math.abs(snap.bank_variance) < 0.01 ? "good" : "bad")}>${fmt.currency(snap.bank_variance)}</span></div>
            <div className="snap-line"><span>GL variance</span><span className={"v " + (Math.abs(snap.gl_variance) < 0.01 ? "good" : "bad")}>${fmt.currency(snap.gl_variance)}</span></div>
            <div className="snap-line"><span>Computed</span><span className="muted" style={{ fontSize: 12 }}>{fmt.dateTime(snap.created_at)}</span></div>
          </div>
        )}

        {blockers.length === 0 ? (
          <div className="blockers-card empty"><h4>✓ Ready for completion</h4>
            <div style={{ fontSize: 12.5 }}>All blockers cleared. Sign-off creates a frozen reconciliation snapshot and a Posted Population snapshot.</div></div>
        ) : (
          <div className="blockers-card">
            <h4>{blockers.length} blocker(s) remaining</h4>
            <ul>{blockers.map((b, i) => <li key={i}>{b.message}</li>)}</ul>
          </div>
        )}

        {!isComplete && (
          <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
            <button className="btn primary" disabled={blockers.length > 0} onClick={() => onComplete(false)} data-testid="complete-btn">
              Complete Reconciliation
            </button>
            <button className="btn" disabled={blockers.length > 0} onClick={() => onComplete(true)} data-testid="complete-and-lock-btn">
              Complete & Lock
            </button>
          </div>
        )}
        {isComplete && (
          <div style={{ marginTop: 14, padding: 12, background: "var(--accent-soft)", borderRadius: 6, fontSize: 13 }}>
            ✓ Completed {fmt.dateTime(period.completed_at)} by {period.completed_by}
            {period.locked_at && <> · Locked {fmt.dateTime(period.locked_at)}</>}
          </div>
        )}
        {isComplete && !isLocked && (
          <div
            data-testid="lock-required-banner"
            style={{
              marginTop: 12, padding: "12px 14px",
              background: "#fff8e1", border: "1px solid #f0c36d",
              borderRadius: 8, display: "flex", gap: 12, alignItems: "center",
              justifyContent: "space-between", flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 13, color: "#7a5900", maxWidth: 620 }}>
              <strong>Not yet locked.</strong> The Certification Package will
              return <em>NOT CERTIFIED</em> until the period is locked. Locking
              is irreversible in the normal flow (needs an explicit unlock,
              which is audit-trailed).
            </div>
            <button
              className="btn primary"
              onClick={onLock}
              data-testid="lock-period-btn"
            >
              Lock Period
            </button>
          </div>
        )}

        {/* Workspace integration for the Certification Package.
            Both deliverables are exposed; the Cert Package is the primary
            controller deliverable, Signed Binder is supporting operational. */}
        <CertificationDeliverables period={period} />
      </div>

      <RecurringJournalsReadinessCard
        periodMonthKey={
          period && period.period_year && period.period_month
            ? `${period.period_year}-${String(period.period_month).padStart(2, "0")}`
            : null
        }
      />

      {posted && (
        <div className="card" style={{ marginTop: 16 }} data-testid="posted-population-card">
          <div className="heading h3">Posted Population</div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
            Certified set plus posted journal entries — the final post-adjustment balance.
          </div>
          <div className="snap-line"><span>Certified GL count</span><span className="mono">{posted.gl_certified_count}</span></div>
          <div className="snap-line"><span>Posted CAJEs</span><span className="mono">{posted.posted_caje_count} · ${fmt.currency(posted.posted_caje_total)}</span></div>
          <div className="snap-line"><span>Posted BSJEs</span><span className="mono">{posted.posted_bsje_count} · ${fmt.currency(posted.posted_bsje_total)}</span></div>
          <div className="snap-line"><span>Posted DRJEs</span><span className="mono">{posted.posted_drje_count} · ${fmt.currency(posted.posted_drje_total)}</span></div>
          <div className="snap-line" style={{ marginTop: 8, borderTop: "1px solid var(--line)", paddingTop: 8 }}>
            <span style={{ fontWeight: 600 }}>GL Post-Adjustment Closing</span>
            <span className="mono" style={{ fontWeight: 600 }}>${fmt.currency(posted.gl_post_adjustment_closing)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Side panes ─────────────────────────────────────────────────────────────

function SnapshotPane({ snap, bankSnap, glSnap, posted, importBatches, period, tab, setTab,
                       excludedCount = 0, onJumpToExcluded,
                       onCertify, onCompute, busy }) {
  return (
    <div className="snapshot" data-testid="snapshot-pane">
      <div className="snapshot-tabs">
        {SOURCES.map(t => (
          <button key={t}
                  className={"tab-btn" + (t === tab ? " active" : "")}
                  onClick={() => setTab(t)}
                  disabled={t === "posted" && !posted}
                  data-testid={`snap-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="snapshot-body">
        {tab === "source" && <SnapSource batches={importBatches} />}
        {tab === "imported" && <SnapImported period={period} />}
        {tab === "certified" && <SnapCertified bank={bankSnap} gl={glSnap} period={period} onCertify={onCertify} busy={busy} />}
        {tab === "posted" && <SnapPosted posted={posted} />}

        <div className="snap-divider" />
        <div className="variance-line snap-line">
          <span>Bank variance</span>
          <span className={"v " + (snap && Math.abs(snap.bank_variance) < 0.01 ? "good" : "bad")}>
            {snap ? `$${fmt.currency(snap.bank_variance)}` : "—"}
          </span>
        </div>
        <div className="variance-line snap-line">
          <span>GL variance</span>
          <span className={"v " + (snap && Math.abs(snap.gl_variance) < 0.01 ? "good" : "bad")}>
            {snap ? `$${fmt.currency(snap.gl_variance)}` : "—"}
          </span>
        </div>
        {snap && period?.materiality_threshold != null && (() => {
          const mat = Number(period.materiality_threshold || 0);
          const worst = Math.max(Math.abs(snap.bank_variance || 0), Math.abs(snap.gl_variance || 0));
          if (worst < 0.01) return null;
          const gap = worst - mat;
          const under = gap <= 0;
          return (
            <div
              data-testid="materiality-nudge"
              className="snap-line"
              style={{
                marginTop: 4,
                fontSize: 11.5,
                color: under ? "#3d7a48" : "#8a5a00",
              }}
            >
              <span>{under
                ? `Within $${fmt.currency(mat)} materiality`
                : `Need to close $${fmt.currency(gap)} more to reach $${fmt.currency(mat)} materiality`}</span>
              <span></span>
            </div>
          );
        })()}
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-sm" style={{ width: "100%" }} onClick={onCompute} disabled={busy} data-testid="snap-compute-btn">
            Recompute Reconciliation
          </button>
        </div>
        {excludedCount > 0 && onJumpToExcluded && (
          <div style={{ marginTop: 8 }}>
            <button
              className="btn-link"
              onClick={onJumpToExcluded}
              data-testid="snap-jump-excluded"
              style={{
                width: "100%",
                background: "none",
                border: "1px dashed var(--line)",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12,
                color: "var(--ink-mute)",
                cursor: "pointer",
                textAlign: "center",
              }}
              title="Jump to the Review step filtered to excluded transactions"
            >
              {excludedCount} excluded — review or restore →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SnapSource({ batches }) {
  if (batches.length === 0) return <div className="muted">No imports yet.</div>;
  const tot = batches.reduce((a, b) => ({
    rows: a.rows + b.source_row_count, dr: a.dr + b.source_total_debits, cr: a.cr + b.source_total_credits,
  }), { rows: 0, dr: 0, cr: 0 });
  return (
    <>
      <div className="snap-line"><span>Files</span><span className="v">{batches.length}</span></div>
      <div className="snap-line"><span>Rows</span><span className="v">{tot.rows}</span></div>
      <div className="snap-line"><span>Source DR</span><span className="v">${fmt.currency(tot.dr)}</span></div>
      <div className="snap-line"><span>Source CR</span><span className="v">${fmt.currency(tot.cr)}</span></div>
    </>
  );
}
function SnapImported({ period }) {
  return <div className="muted" style={{ fontSize: 12 }}>The Imported layer is the immutable parsed set (raw_transactions). View per-transaction in the grid.</div>;
}
function SnapCertified({ bank, gl, period, onCertify, busy }) {
  const isTrulyLocked = period.status === "locked";
  const isComplete = period.status === "complete";
  return (
    <>
      <div className="snap-line"><span>Bank certified</span>
        <span className="v">{bank ? bank.certified_count : "—"}</span></div>
      <div className="snap-line"><span>GL certified</span>
        <span className="v">{gl ? gl.certified_count : "—"}</span></div>
      {bank && <div className="snap-line muted" style={{ fontSize: 11 }}>Lineage {bank.lineage_complete ? "✓" : "incomplete"}</div>}
      {isTrulyLocked ? (
        <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          Period locked — re-certification requires Re-certify period.
        </div>
      ) : isComplete ? (
        <div className="muted" style={{ fontSize: 11, marginTop: 8, color: "#7a5900" }}>
          Completed but not locked. Lock the period from the Complete step
          before generating the Certification Package.
        </div>
      ) : (
        <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
          <button className="btn btn-sm" disabled={busy} onClick={() => onCertify("bank")} data-testid="certify-bank-btn">Certify Bank</button>
          <button className="btn btn-sm" disabled={busy} onClick={() => onCertify("gl")} data-testid="certify-gl-btn">Certify GL</button>
        </div>
      )}
    </>
  );
}
function SnapPosted({ posted }) {
  if (!posted) return <div className="muted">Frozen on Complete.</div>;
  return (
    <>
      <div className="snap-line"><span>Posted CAJEs</span><span className="v">{posted.posted_caje_count}</span></div>
      <div className="snap-line"><span>Posted BSJEs</span><span className="v">{posted.posted_bsje_count}</span></div>
      <div className="snap-line"><span>GL post-adj</span><span className="v">${fmt.currency(posted.gl_post_adjustment_closing)}</span></div>
    </>
  );
}

function BlockersCard({ blockers }) {
  if (!blockers || blockers.length === 0) {
    return <div className="blockers-card empty" data-testid="blockers-empty"><h4>✓ No blockers</h4></div>;
  }
  return (
    <div className="blockers-card" data-testid="blockers-card">
      <h4>{blockers.length} blocker(s)</h4>
      <ul>
        {blockers.map((b, i) => <li key={i}>{b.message}</li>)}
      </ul>
    </div>
  );
}

// ── Drawers ────────────────────────────────────────────────────────────────

function AuditDrawer({ logs }) {
  if (!logs?.length) return <div className="empty">No audit entries yet.</div>;
  return (
    <div>
      {logs.map(l => (
        <div key={l.id} className="audit-entry" data-testid={`audit-${l.id}`}>
          <div className="audit-action">{l.action} · {l.entity_type}</div>
          <div className="audit-details">{l.details}</div>
          <div className="audit-meta">{fmt.dateTime(l.created_at)} · by {l.actor_user_id}</div>
        </div>
      ))}
    </div>
  );
}

function LineageDrawer({ bankLineage, glLineage, posted, period }) {
  return (
    <div>
      {!bankLineage && !glLineage && <div className="empty">Recompute reconciliation to populate the lineage.</div>}
      {bankLineage && <BalanceLineage title="Bank Lineage" lineage={bankLineage} />}
      {glLineage && <BalanceLineage title="GL Lineage" lineage={glLineage} />}
      {posted && (
        <div className="lineage">
          <div className="heading h3" style={{ marginBottom: 8 }}>Posted Position (post-adjustment ERP balance)</div>
          <div className="lineage-line"><span className="lineage-label">Certified GL Closing</span><span className="lineage-value">${fmt.currency(period.gl_closing_balance)}</span></div>
          <div className="lineage-line"><span className="lineage-label">+ Posted CAJEs</span><span className="lineage-value">${fmt.currency(posted.posted_caje_total)}</span></div>
          <div className="lineage-line"><span className="lineage-label">+ Posted BSJEs</span><span className="lineage-value">${fmt.currency(posted.posted_bsje_total)}</span></div>
          <div className="lineage-line"><span className="lineage-label">+ Posted DRJEs</span><span className="lineage-value">${fmt.currency(posted.posted_drje_total)}</span></div>
          <div className="lineage-line total"><span>= Posted Closing (ERP)</span><span>${fmt.currency(posted.gl_post_adjustment_closing)}</span></div>
        </div>
      )}
    </div>
  );
}

function BalanceLineage({ title, lineage }) {
  return (
    <div className="lineage">
      <div className="heading h3" style={{ marginBottom: 8 }}>{title}</div>
      <div className="lineage-line"><span className="lineage-label">Opening Balance</span><span className="lineage-value">${fmt.currency(lineage.opening_balance)}</span></div>
      <div className="lineage-line"><span className="lineage-label">+ Certified Debits</span><span className="lineage-value">${fmt.currency(lineage.debits_total)}</span></div>
      <div className="lineage-line"><span className="lineage-label">− Certified Credits</span><span className="lineage-value">(${fmt.currency(lineage.credits_total)})</span></div>
      <div className="lineage-line subtotal"><span>= Activity Net</span><span>${fmt.currency(lineage.activity_net)}</span></div>
      {lineage.adjustments_total !== 0 && (
        <>
          <div className="lineage-line"><span className="lineage-label">+ Posted CAJEs</span><span className="lineage-value">${fmt.currency(lineage.posted_caje_total)}</span></div>
          <div className="lineage-line"><span className="lineage-label">+ Posted BSJEs</span><span className="lineage-value">${fmt.currency(lineage.posted_bsje_total)}</span></div>
          <div className="lineage-line"><span className="lineage-label">+ Posted DRJEs</span><span className="lineage-value">${fmt.currency(lineage.posted_drje_total)}</span></div>
        </>
      )}
      <div className="lineage-line total"><span>Calculated Closing</span><span>${fmt.currency(lineage.calculated_closing)}</span></div>
      <div className="lineage-line"><span className="lineage-label">− Certified Closing</span><span className="lineage-value">(${fmt.currency(lineage.certified_closing)})</span></div>
      <div className="lineage-line total"><span>Variance</span>
        <span style={{ color: Math.abs(lineage.variance) < 0.01 ? "var(--accent)" : "var(--blocker)" }}>${fmt.currency(lineage.variance)}</span></div>
    </div>
  );
}

function KeyboardHint() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="kbd-hint" data-testid="kbd-hint"
         style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12 }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="kbd-hint-toggle"
        aria-expanded={open}
        aria-label="Keyboard shortcuts"
        style={{
          background: "transparent", border: "1px solid var(--line)",
          borderRadius: 999, padding: "2px 10px", cursor: "pointer",
          fontSize: 11, color: "var(--ink-mute)", letterSpacing: "0.04em",
        }}
      >
        Keyboard shortcuts <kbd style={{ marginLeft: 6 }}>?</kbd>
      </button>
      {open && (
        <div role="dialog" data-testid="kbd-hint-popover" style={{
          position: "fixed", right: 16, bottom: 48, zIndex: 50,
          background: "var(--surface)", border: "1px solid var(--line)",
          borderRadius: 8, padding: "14px 18px", boxShadow: "0 8px 24px rgba(15,17,21,0.10)",
          fontSize: 12, color: "var(--ink)", minWidth: 240,
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.06em", color: "var(--ink-mute)", marginBottom: 8,
          }}>Keyboard shortcuts</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 }}>
            <li><kbd>J</kbd> / <kbd>K</kbd> — next / previous transaction</li>
            <li><kbd>Space</kbd> — select</li>
            <li><kbd>M</kbd> — create match group</li>
            <li><kbd>C</kbd> — classify</li>
            <li><kbd>E</kbd> — exclude</li>
            <li><kbd>L</kbd> — open activity log</li>
            <li><kbd>B</kbd> — open balance lineage</li>
            <li><kbd>X</kbd> — clear selection</li>
            <li><kbd>1</kbd>–<kbd>4</kbd> — jump to step</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Workspace integration · Certification Package + Signed Binder ─────────
// The Certification Package (primary) is rendered by the same Phase 4
// reports orchestrator the Reports page uses — we reuse `api.runReport`
// so no PDF logic is duplicated. The Signed Binder (supporting) keeps its
// existing direct GET link.
function CertificationDeliverables({ period }) {
  // Phase 4 close-out · G-3 — surface missing Company Settings fields
  // (non-blocking warning). Empty/null treated as missing for: company name,
  // preparer name, preparer title. Refreshes on every Workspace mount.
  const [missingFields, setMissingFields] = useState([]);
  useEffect(() => {
    let cancel = false;
    api.getSettings().then(s => {
      if (cancel || !s) return;
      const missing = [];
      if (!(s.company_name || "").trim()) missing.push("Company name");
      const c = s.contact || {};
      if (!(c.contact_name || "").trim()) missing.push("Preparer name");
      if (!(c.contact_position || "").trim()) missing.push("Title");
      setMissingFields(missing);
    }).catch(() => { /* swallow — warning is best-effort */ });
    return () => { cancel = true; };
  }, []);

  // P0 · Feb 2026 — Ad-blocker-safe short URLs.
  // The previous `/certification_package.pdf` URL was matched by aggressive
  // ad-blocker filter lists on the word "package". The new `/cert.pdf` path
  // mirrors the proven-working `/binder.pdf` URL shape exactly:
  // 4-letter benign name, no query string, no semantic words.
  // The backend persists a copy as a GridFS-backed `report_pdf` asset for
  // audit linkage.
  //
  // Cache-busting: prior versions of this PDF get cached at Cloudflare's
  // edge (default rule for application/pdf). Once a period changes state
  // — e.g. lock, unlock, re-certify — the controller MUST see the fresh
  // PDF, not the cached one. The `updated_at` or `locked_at` timestamp
  // makes each state a distinct URL from the CDN's point of view, so any
  // state change forces a cache miss and a re-render. Falls back to
  // `Date.now()` when the period doesn't have those timestamps yet.
  const stateStamp = (
    period.locked_at || period.updated_at || period.completed_at || Date.now()
  );
  const stateKey = encodeURIComponent(String(stateStamp));
  const certPackageUrl =
    `${process.env.REACT_APP_BACKEND_URL}` +
    `/api/periods/${period.id}/cert.pdf?v=${stateKey}`;
  const binderUrl =
    `${process.env.REACT_APP_BACKEND_URL}` +
    `/api/periods/${period.id}/binder.pdf?v=${stateKey}`;

  return (
    <div style={{ marginTop: 18 }} data-testid="certification-deliverables">
      {/* Primary · Certification Package */}
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--ink-mute)", fontWeight: 600, marginBottom: 6,
      }}>
        Certification
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <a
          className="btn primary"
          href={certPackageUrl}
          target="_blank" rel="noopener noreferrer"
          data-testid="download-certification-package-btn"
        >
          Download Certification Package (PDF)
        </a>
        <span className="muted" style={{ fontSize: 12 }}>
          Controller deliverable · Executive Summary on page 1, full §1–§4 + appendices.
        </span>
      </div>

      {/* Phase 4 close-out · G-3 — non-blocking Settings completeness warning. */}
      {missingFields.length > 0 && (
        <div
          data-testid="settings-incomplete-warning"
          style={{
            marginTop: 10, padding: "8px 12px",
            background: "#fff8e1", border: "1px solid #f0d780",
            color: "#5b4400", borderRadius: 6, fontSize: 12, lineHeight: 1.45,
          }}
        >
          <strong>Company Settings incomplete.</strong> The package will still
          generate, but the following field{missingFields.length === 1 ? " is" : "s are"} blank
          and will appear empty in the Certification Package: {" "}
          <strong>{missingFields.join(" · ")}</strong>.{" "}
          <Link to="/admin/company-settings" data-testid="settings-warning-link"
                style={{ color: "#5b4400", textDecoration: "underline" }}>
            Open Company Settings →
          </Link>
        </div>
      )}

      {/* Supporting · Signed Binder */}
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--ink-mute)", fontWeight: 600, marginTop: 16, marginBottom: 6,
      }}>
        Supporting Documentation
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <a
          className="btn"
          href={binderUrl}
          target="_blank" rel="noopener noreferrer"
          data-testid="download-binder-btn"
        >
          Download Signed Binder (PDF)
        </a>
        <span className="muted" style={{ fontSize: 12 }}>
          Legacy permanent reference package — kept for operational continuity.
        </span>
      </div>
    </div>
  );
}



// ── Bank Statement Certification drawer ────────────────────────────────────
// History across every bank ImportBatch in the period. Each batch has its
// own panel that reveals its latest pipeline result.
function BankCertDrawer({ periodId, bankBatches, showToast, onChanged }) {
  if (!bankBatches || bankBatches.length === 0) {
    return <div className="empty">No bank imports in this period yet.</div>;
  }
  return (
    <div data-testid="bankcert-drawer">
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        Bank Statement Certification runs per bank ImportBatch. Every statement
        certification is preserved (append-only audit trail).
      </div>
      {bankBatches.map(b => (
        <div key={b.id} className="card" style={{ marginBottom: 12, padding: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{b.file_name}</div>
              <div className="muted" style={{ fontSize: 11 }}>
                Batch imported {fmt.dateTime(b.created_at)}
              </div>
            </div>
          </div>
          <BankStatementCertificationPanel
            batchId={b.id}
            showToast={showToast}
            onChanged={onChanged}
          />
        </div>
      ))}
    </div>
  );
}
